import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

import { filesize } from 'filesize';

import Lightbox from 'react-18-image-lightbox';

import download from '../../../utils/download';

import ImageComponent from './ImageComponent';

import getTeamsUrl from '../../../utils/getTeamsUrl';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogHeader,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/ui/custom/IconButton';
import { ArrowDownToLine, Loader2 } from 'lucide-react';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { BottomSheetTextButton } from '@/components/mobile';
import {
	PHONE_DIALOG_CLASS,
	PHONE_DIALOG_HEADER_CLASS,
	PHONE_DIALOG_TITLE_CLASS,
} from './phoneDialog';

export default function AttachmentsModal(props) {
	const {
		handleIsAttachmentsModalOpen,
		isAttachmentsModalOpen,
		_teamConversation,
	} = props;

	const [attachments, setAttachments] = useState([]);
	const [loading, setLoading] = useState(false);

	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [photoIndex, setPhotoIndex] = useState(0);
	const isMobile = useIsMobile();

	// The loader reads the list through a ref. When it depended on the list
	// itself, every page it loaded re-ran the effect below and fetched the
	// next one, forever (hundreds of requests while any media was shown).
	const attachmentsRef = useRef(attachments);
	attachmentsRef.current = attachments;
	const latestRequest = useRef(0);

	const loadMoreAttachments = useCallback(
		async ({ reset = false } = {}) => {
			const request = latestRequest.current + 1;
			latestRequest.current = request;
			setLoading(true);

			const current = reset ? [] : attachmentsRef.current;
			const earliestAttachment = [...current].sort(
				(a, b) => new Date(a.createdAt) - new Date(b.createdAt)
			)[0];

			try {
				const res = await axios.post(
					`${getTeamsUrl(window.location.href)}/api/get-attachments`,
					{
						earliestAttachmentDate: earliestAttachment
							? earliestAttachment.createdAt
							: new Date().toUTCString(),

						_teamConversation,
					}
				);

				// a reply for another conversation (or an older page) is dropped
				if (latestRequest.current !== request) return;

				const { data } = res;

				if (data && data.attachments) {
					setAttachments((prev) => [
						...(reset ? [] : prev),
						...data.attachments,
					]);
				}
			} catch (err) {
				console.log('Error loading attachments:', err);
			} finally {
				if (latestRequest.current === request) {
					setLoading(false);
				}
			}
		},
		[_teamConversation]
	);

	// fetch the first page once per conversation
	useEffect(() => {
		loadMoreAttachments({ reset: true });
	}, [loadMoreAttachments]);

	const handleLoadMoreClick = () => {
		loadMoreAttachments();
	};

	const saveImage = (attachment, imageBlob) => {
		setAttachments((prevAttachments) => {
			const attachmentIndex = prevAttachments.findIndex(
				(a) => a.url === attachment.url
			);

			if (attachmentIndex === -1) {
				return prevAttachments;
			}

			const newAttachments = [...prevAttachments];

			newAttachments[attachmentIndex] = {
				...newAttachments[attachmentIndex],
				imageBlob,
			};

			return newAttachments;
		});
	};

	const openLightbox = (index) => {
		setPhotoIndex(index);
		setIsLightboxOpen(true);
	};

	return (
		<>
			{isLightboxOpen && (
				<Lightbox
					mainSrc={attachments[photoIndex].imageBlob}
					nextSrc={attachments[(photoIndex + 1) % attachments.length]}
					prevSrc={
						attachments[
							(photoIndex + attachments.length - 1) %
								attachments.length
						]
					}
					onCloseRequest={() => setIsLightboxOpen(false)}
					reactModalStyle={{ zIndex: 99999 }}
					onMovePrevRequest={() =>
						setPhotoIndex(
							(photoIndex + attachments.length - 1) %
								attachments.length
						)
					}
					onMoveNextRequest={() =>
						setPhotoIndex(
							(photoIndex + attachments.length + 1) %
								attachments.length
						)
					}
				/>
			)}
			<Dialog
				onOpenChange={handleIsAttachmentsModalOpen}
				// aria-labelledby="simple-dialog-title"
				open={isAttachmentsModalOpen}
			>
				<DialogContent mobileVariant="fullscreen" hideClose="mobile" className={PHONE_DIALOG_CLASS}>
					<DialogHeader className={PHONE_DIALOG_HEADER_CLASS}>
						{isMobile && <span aria-hidden />}
						<DialogTitle
							id="simple-dialog-title"
							className={PHONE_DIALOG_TITLE_CLASS}
						>
							Group chat attachments
						</DialogTitle>
						{isMobile && (
							<BottomSheetTextButton
								strong
								className="justify-self-end"
								onClick={() => handleIsAttachmentsModalOpen(false)}
							>
								Done
							</BottomSheetTextButton>
						)}
					</DialogHeader>

					<ScrollArea className="h-72 max-md:h-auto max-md:min-h-0 max-md:flex-1">
						<div className="flex flex-col space-y-1 w-full max-md:px-2 max-md:py-2">
							{attachments
								.sort(
									(a, b) =>
										new Date(b.createdAt) -
										new Date(a.createdAt)
								)
								.map((attachment, index) => (
									<div
										key={attachment.url}
										className={cn(
											'space-between flex items-center hover:bg-stone-100 dark:hover:bg-stone-800 rounded-md cursor-pointer p-2'
										)}
									>
										<div
											style={{
												display: 'flex',
												marginRight: '16px',
											}}
											className="w-40 h-90 max-md:!mr-3 max-md:size-16 max-md:shrink-0 max-md:overflow-hidden max-md:rounded-md"
										>
											<ImageComponent
												src={attachment.url}
												name={attachment.name}
												item={attachment}
												onImageLoad={(imageBlob) =>
													saveImage(
														attachment,
														imageBlob
													)
												}
												openLightbox={() =>
													openLightbox(index)
												}
												isInMessageContainer
											/>
										</div>
										<div className="max-md:min-w-0 max-md:flex-1">
											<p className="break-words text-sm text-wrap text-ellipsis w-40 line-clamp-3 max-md:w-auto max-md:line-clamp-2">
												{attachment.name}
											</p>
											<p className="text-xs">
												{attachment.type}
											</p>
											<p className="text-xs text-muted-foreground">
												{filesize(
													Number(
														attachment.size || 0
													),
													{
														base: 2,
														standard: 'jedec',
													}
												)}
											</p>
										</div>

										<IconButton
											onClick={() => {
												download(
													attachment.imageBlob,
													attachment.name
												);
											}}
											aria-label={`Download ${attachment.name || 'attachment'}`}
											className="hover:bg-[#000000] hover:bg-opacity-10 ml-2 max-md:shrink-0"
										>
											<ArrowDownToLine className="size-4 text-muted-foreground" />
										</IconButton>
									</div>
								))}
						</div>
					</ScrollArea>
					<CustomButton
						size="lg"
						variant="secondary"
						onClick={() => handleLoadMoreClick()}
						disabled={loading}
						className="max-md:mx-4 max-md:mb-[max(1rem,var(--safe-bottom))] max-md:mt-2 max-md:shrink-0"
					>
						{loading ? (
							<Loader2 className="flex justify-center items-center w-full size-6 text-primary animate-spin" />
						) : (
							'Load More'
						)}
					</CustomButton>
				</DialogContent>
			</Dialog>
		</>
	);
}
